import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { memberApi, setMemberToken } from "../lib/memberApi";
import { toast } from "sonner";
import { Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function MemberLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await memberApi.login({ email, password });
      setMemberToken(data.token);
      toast.success(`Welcome back, ${data.member.name.split(' ')[0]}.`);
      if (data.member.active) navigate("/member/dashboard");
      else navigate("/membership");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="member-login-page">
      <header className="border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_houston-training-hub/artifacts/5f1feyf5_1781429554579.png" alt="Gain Time Fitness" className="w-8 h-8 object-cover shrink-0" />
            <span className="font-display text-lg tracking-wider">GAIN TIME</span>
          </Link>
          <Link to="/membership" className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E5B43A]">
            New here?
          </Link>
        </div>
      </header>

      <main className="grid place-items-center min-h-[calc(100vh-4rem)] px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md border border-zinc-800 bg-[#141414] p-8 md:p-10" data-testid="member-login-form">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 bg-[#E5B43A] grid place-items-center">
              <Lock size={18} className="text-black" />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Member</div>
              <div className="font-display text-xl tracking-wider">Sign In</div>
            </div>
          </div>

          <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
            className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 mb-5 text-white focus:outline-none focus:border-[#E5B43A]"
          />

          <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Password</label>
          <div className="relative mb-3">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 pr-12 text-white focus:outline-none focus:border-[#E5B43A]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="toggle-password"
              className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-[#E5B43A]"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="flex justify-end mb-6">
            <Link to="/member/forgot-password" data-testid="forgot-password-link" className="text-xs tracking-widest uppercase text-zinc-500 hover:text-[#E5B43A] transition-colors">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in..." : <>Sign In <ArrowRight size={16} /></>}
          </button>

          <div className="mt-6 text-center text-xs text-zinc-500">
            Not a member yet?{" "}
            <Link to="/membership" className="text-[#E5B43A] hover:underline">Pick a tier</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
